import { useState } from "react";
import { useNavigate } from "react-router-dom";
import { useTranslation } from "react-i18next";
import { Dialog, DialogContent, DialogHeader, DialogTitle } from "@/components/ui/dialog";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { LogIn, Mail, ArrowRight } from "lucide-react";

interface AuthGateDialogProps {
  open: boolean;
  onOpenChange: (open: boolean) => void;
  onGuestContinue: (email: string) => void;
}

const AuthGateDialog = ({ open, onOpenChange, onGuestContinue }: AuthGateDialogProps) => {
  const { t } = useTranslation("common");
  const navigate = useNavigate();
  const [guestEmail, setGuestEmail] = useState("");
  const [showGuestForm, setShowGuestForm] = useState(false);
  const [emailError, setEmailError] = useState("");

  const handleLoginRedirect = () => {
    onOpenChange(false);
    navigate("/auth?redirect=/guest/library");
  };

  const handleGuestSubmit = () => {
    const emailRegex = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;
    if (!emailRegex.test(guestEmail)) {
      setEmailError(t("videos_page.invalid_email", "Please enter a valid email address"));
      return;
    }
    setEmailError("");
    onGuestContinue(guestEmail);
    onOpenChange(false);
  };

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="max-w-md">
        <DialogHeader>
          <DialogTitle className="text-center text-lg">
            {t("videos_page.auth_gate_title", "How would you like to continue?")}
          </DialogTitle>
        </DialogHeader>

        <div className="space-y-4 pt-2">
          {/* Option 1: Register / Log In */}
          <Button
            variant="default"
            className="w-full h-auto py-4 flex items-center gap-3"
            onClick={handleLoginRedirect}
          >
            <LogIn className="h-5 w-5 shrink-0" />
            <div className="text-left">
              <p className="font-semibold text-sm">
                {t("videos_page.register_login", "Register / Log In")}
              </p>
              <p className="text-xs opacity-80 font-normal">
                {t("videos_page.register_login_desc", "Create an account to access your purchases anytime")}
              </p>
            </div>
            <ArrowRight className="h-4 w-4 shrink-0 ml-auto" />
          </Button>

          <div className="relative">
            <div className="absolute inset-0 flex items-center">
              <span className="w-full border-t border-border" />
            </div>
            <div className="relative flex justify-center text-xs uppercase">
              <span className="bg-background px-2 text-muted-foreground">
                {t("videos_page.or", "or")}
              </span>
            </div>
          </div>

          {/* Option 2: Continue as Guest */}
          {!showGuestForm ? (
            <Button
              variant="outline"
              className="w-full h-auto py-4 flex items-center gap-3"
              onClick={() => setShowGuestForm(true)}
            >
              <Mail className="h-5 w-5 shrink-0" />
              <div className="text-left">
                <p className="font-semibold text-sm">
                  {t("videos_page.continue_guest", "Continue as Guest")}
                </p>
                <p className="text-xs text-muted-foreground font-normal">
                  {t("videos_page.continue_guest_desc", "Buy with just your email — no account needed")}
                </p>
              </div>
              <ArrowRight className="h-4 w-4 shrink-0 ml-auto" />
            </Button>
          ) : (
            <div className="space-y-3 border rounded-lg p-4">
              <Label htmlFor="guest-email" className="text-sm">
                {t("videos_page.guest_email_label", "Your email address")}
              </Label>
              <Input
                id="guest-email"
                type="email"
                placeholder={t("videos_page.email_placeholder")}
                value={guestEmail}
                onChange={(e) => {
                  setGuestEmail(e.target.value);
                  setEmailError("");
                }}
                onKeyDown={(e) => e.key === "Enter" && handleGuestSubmit()}
              />
              {emailError && (
                <p className="text-xs text-destructive">{emailError}</p>
              )}
              <Button className="w-full" onClick={handleGuestSubmit}>
                {t("videos_page.proceed_checkout", "Proceed to Checkout")}
              </Button>
            </div>
          )}
        </div>
      </DialogContent>
    </Dialog>
  );
};

export default AuthGateDialog;
